"use client"

import type React from "react"
import { useState } from "react"
import { FiCalendar, FiMapPin, FiUpload, FiX } from "react-icons/fi"
import FormInput from "../../ui/form-input"
import CustomSelect from "../../ui/custom-select"
import FormCheckbox from "../../ui/form-checkbox"
import api from "../../libs/axios"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"
import ModalSelect from "../../ui/select-modal"

// Tournament enums based on your controller
export enum TournamentCategory {
  OPEN = 'open',
  CORPORATE = 'corporate',
  COMMUNITY = 'community',
  SCHOOL = 'school',
  COLLEGE = 'college',
  UNIVERSITY = 'university',
  SERIES = 'series',
  OTHER = 'other'
}

export enum TournamentStatus {
  DRAFT = "draft",
  REGISTRATION_OPEN = "registration_open",
  REGISTRATION_CLOSED = "registration_closed",
  ONGOING = "ongoing",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export enum BallType {
  LEATHER = "leather",
  TENNIS = "tennis",
  OTHER = "other",
}

export enum PitchType {
  ROUGH = "rough",
  CEMENT = "cement",
  TURF = "turf",
  MATTING = "matting",
  ASTROTURF = "astroturf",
}

const tournamentCategories = [
  { value: TournamentCategory.OPEN, label: "Open", description: "Open to all players" },
  { value: TournamentCategory.CORPORATE, label: "Corporate", description: "Corporate teams" },
  { value: TournamentCategory.COMMUNITY, label: "Community", description: "Community teams" },
  { value: TournamentCategory.SCHOOL, label: "School", description: "School teams" },
  { value: TournamentCategory.COLLEGE, label: "College", description: "College teams" },
  { value: TournamentCategory.UNIVERSITY, label: "University", description: "University teams" },
  { value: TournamentCategory.SERIES, label: "Series", description: "Series tournament" },
  { value: TournamentCategory.OTHER, label: "Other", description: "Other type of tournament" },
]

const tournamentStatuses = [
  { value: TournamentStatus.DRAFT, label: "Draft", description: "Tournament in draft mode" },
  { value: TournamentStatus.REGISTRATION_OPEN, label: "Registration Open", description: "Open for team registration" },
  { value: TournamentStatus.REGISTRATION_CLOSED, label: "Registration Closed", description: "Registration period ended" },
  { value: TournamentStatus.ONGOING, label: "Ongoing", description: "Tournament in progress" },
  { value: TournamentStatus.COMPLETED, label: "Completed", description: "Tournament finished" },
  { value: TournamentStatus.CANCELLED, label: "Cancelled", description: "Tournament cancelled" },
]

const ballTypes = [
  { value: BallType.LEATHER, label: "Leather Ball", description: "Traditional leather cricket ball" },
  { value: BallType.TENNIS, label: "Tennis Ball", description: "Tennis ball for casual play" },
  { value: BallType.OTHER, label: "Other", description: "Other type of ball" },
]

const pitchTypes = [
  { value: PitchType.ROUGH, label: "Rough Pitch", description: "Rough surface" },
  { value: PitchType.CEMENT, label: "Cement Pitch", description: "Cement surface" },
  { value: PitchType.TURF, label: "Turf Pitch", description: "Natural turf surface" },
  { value: PitchType.MATTING, label: "Matting Pitch", description: "Matting surface" },
  { value: PitchType.ASTROTURF, label: "Astroturf Pitch", description: "Artificial grass surface" },
]

const matchTypes = [
  { value: "limited_overs", label: "Limited Overs", description: "Limited overs format" },
  { value: "box_cricket", label: "Box Cricket", description: "Box cricket format" },
  { value: "pair_cricket", label: "Pair Cricket", description: "Pair cricket format" },
  { value: "test_match", label: "Test Match", description: "Test match format" },
  { value: "the_hundred", label: "The Hundred", description: "The Hundred format" },
]

export interface ITournamentFormData {
  name: string
  description: string
  organizer_id: string
  tournament_category: TournamentCategory
  status: TournamentStatus
  start_date: string
  end_date?: string
  registration_deadline?: string
  max_teams?: string
  entry_fee?: string
  prize_pool?: string
  location?: string
  rules?: string
  banner_url?: string
  tournament_logo_url?: string
  last_batter_rule: boolean
  pitch_type: PitchType
  ball_type: BallType
  match_type: string
  ground_id: string | number
  more_teams: boolean
  match_officials: boolean
}

export default function CreateTournament() {
  const navigate = useNavigate()

  // Form states
  const [formData, setFormData] = useState<ITournamentFormData>({
    name: "",
    description: "",
    organizer_id: "",
    tournament_category: TournamentCategory.OPEN,
    status: TournamentStatus.DRAFT,
    start_date: "",
    end_date: "",
    registration_deadline: "",
    max_teams: "",
    entry_fee: "",
    prize_pool: "",
    location: "",
    rules: "",
    banner_url: "",
    tournament_logo_url: "",
    last_batter_rule: false,
    pitch_type: PitchType.TURF,
    ball_type: BallType.LEATHER,
    match_type: "",
    ground_id: "",
    more_teams: false,
    match_officials: false,
  })

  // File upload states
  const [bannerFile, setBannerFile] = useState<File | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [bannerPreview, setBannerPreview] = useState<string>("")
  const [logoPreview, setLogoPreview] = useState<string>("")
  const [bannerUploading, setBannerUploading] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)

  const [errors, setErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  // File upload function
  const uploadFile = async (file: File, type: 'tournament_logos' | 'tournament_banners'): Promise<string> => {
    const uploadFormData = new FormData();
    uploadFormData.append('type', type);
    uploadFormData.append('files', file);

    try {
      const response = await api.post('/media/upload', uploadFormData);
      const uploadedFiles = response.data?.files || [];

      if (!uploadedFiles.length) {
        throw new Error('Failed to upload file');
      }

      return uploadedFiles[0].url;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      throw new Error(error?.response?.data?.message || 'Failed to upload file');
    }
  };

  // Handle banner file selection
  const handleBannerFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB')
      return
    }

    setBannerFile(file)

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setBannerPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Handle logo file selection
  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB')
      return
    }

    setLogoFile(file)

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Remove banner
  const removeBanner = () => {
    setBannerFile(null)
    setBannerPreview("")
    setFormData(prev => ({ ...prev, banner_url: "" }))
  }

  // Remove logo
  const removeLogo = () => {
    setLogoFile(null)
    setLogoPreview("")
    setFormData(prev => ({ ...prev, tournament_logo_url: "" }))
  }

  const validateForm = () => {
    const newErrors: any = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Tournament name is required"
    } else if (formData.name.length < 3 || formData.name.length > 150) {
      newErrors.name = "Tournament name must be between 3-150 characters"
    }

    // Organizer validation
    if (!formData.organizer_id) {
      newErrors.organizer_id = "Tournament organizer is required"
    }

    // Category validation
    if (!formData.tournament_category) {
      newErrors.tournament_category = "Tournament category is required"
    }

    // Start date validation
    if (!formData.start_date) {
      newErrors.start_date = "Start date is required"
    } else {
      const startDate = new Date(formData.start_date)
      const now = new Date()
      now.setHours(0, 0, 0, 0) // Reset time to compare dates only
      if (startDate < now) {
        newErrors.start_date = "Start date cannot be in the past"
      }
    }

    // End date validation
    if (!formData.end_date) {
      newErrors.end_date = "End date is required"
    } else {
      const startDate = new Date(formData.start_date)
      const endDate = new Date(formData.end_date)
      if (endDate <= startDate) {
        newErrors.end_date = "End date must be after start date"
      }
    }

    // Registration deadline validation
    if (formData.registration_deadline) {
      const regDeadline = new Date(formData.registration_deadline)
      const startDate = new Date(formData.start_date)
      if (regDeadline >= startDate) {
        newErrors.registration_deadline = "Registration deadline must be before start date"
      }

      // Check if registration deadline is in the past
      const now = new Date()
      if (regDeadline < now) {
        newErrors.registration_deadline = "Registration deadline cannot be in the past"
      }
    }

    // Max teams validation
    if (formData.max_teams) {
      const maxTeams = Number.parseInt(formData.max_teams.toString())
      if (maxTeams < 2 || maxTeams > 128) {
        newErrors.max_teams = "Max teams must be between 2 and 128"
      }
    }

    // Match type validation
    if (formData.match_type === "") {
      newErrors.match_type = "Match type is required"
    }

    // Entry fee validation
    if (formData.entry_fee) {
      const entryFee = Number.parseFloat(formData.entry_fee.toString())
      if (entryFee < 0) {
        newErrors.entry_fee = "Entry fee cannot be negative"
      }
    }

    // Prize pool validation
    if (formData.prize_pool) {
      const prizePool = Number.parseFloat(formData.prize_pool.toString())
      if (prizePool < 0) {
        newErrors.prize_pool = "Prize pool cannot be negative"
      }
    }

    if (!formData.ground_id) {
      newErrors.ground_id = "Ground selection is required"
    }

    // Location validation
    if (!formData.location || !formData.location.trim()) {
      newErrors.location = "Location is required"
    } else if (formData.location.length > 500) {
      newErrors.location = "Location must be less than 500 characters"
    }

    if (!formData.ball_type) {
      newErrors.ball_type = "Ball type is required";
    }
    if (!formData.pitch_type) {
      newErrors.pitch_type = "Pitch type is required";
    }

    // Rules validation
    if (formData.rules && formData.rules.length > 2000) {
      newErrors.rules = "Rules must be less than 2000 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      let bannerUrl = formData.banner_url
      let logoUrl = formData.tournament_logo_url

      // Upload banner if new file selected
      if (bannerFile) {
        setBannerUploading(true)
        try {
          bannerUrl = await uploadFile(bannerFile, 'tournament_banners')
        } catch (error: any) {
          toast.error('Failed to upload banner: ' + error.message)
          setBannerUploading(false)
          setLoading(false)
          return
        }
        setBannerUploading(false)
      }

      // Upload logo if new file selected
      if (logoFile) {
        setLogoUploading(true)
        try {
          logoUrl = await uploadFile(logoFile, 'tournament_logos')
        } catch (error: any) {
          toast.error('Failed to upload logo: ' + error.message)
          setLogoUploading(false)
          setLoading(false)
          return
        }
        setLogoUploading(false)
      }

      const payload: any = {
        name: formData.name,
        description: formData.description || null,
        organizer_id: Number.parseInt(formData.organizer_id.toString()),
        tournament_category: formData.tournament_category,
        status: formData.status,
        start_date: new Date(formData.start_date).toISOString(),
        location: formData.location,
        last_batter_rule: formData.last_batter_rule,
        more_teams: formData.more_teams,
        match_officials: formData.match_officials,
      }

      // Optional fields
      if (formData.end_date) {
        payload.end_date = new Date(formData.end_date).toISOString()
      }

      if (formData.registration_deadline) {
        payload.registration_deadline = new Date(formData.registration_deadline).toISOString()
      }

      if (formData.max_teams) {
        payload.max_teams = Number.parseInt(formData.max_teams.toString())
      }

      if (formData.entry_fee) {
        payload.entry_fee = Number.parseFloat(formData.entry_fee.toString())
      }

      if (formData.prize_pool) {
        payload.prize_pool = Number.parseFloat(formData.prize_pool.toString())
      }

      if (formData.rules) {
        payload.rules = formData.rules
      }

      if (bannerUrl) {
        payload.banner_url = bannerUrl
      }

      if (logoUrl) {
        payload.tournament_logo_url = logoUrl
      }

      if (formData.pitch_type) {
        payload.pitch_type = formData.pitch_type
      }

      if (formData.ball_type) {
        payload.ball_type = formData.ball_type
      }

      if (formData.match_type) {
        payload.match_type = formData.match_type
      }

      if (formData.ground_id) {
        payload.ground_id = Number.parseInt(formData.ground_id.toString())
      }

      // Create new tournament
      const response = await api.post("/tournaments", payload)
      toast.success("Tournament created successfully")

      // Navigate to tournaments list or tournament details
      navigate("/tournaments")
    } catch (error: any) {
      console.error("Error:", error)
      const errorMessage = error.response?.data?.message || `Network Error: ${error.message || "Could not connect to the server."}`
      setApiError(errorMessage)
      toast.error("Failed to create tournament")
    } finally {
      setLoading(false)
    }
  }

  // Fetch organizers function for dropdown
  const fetchOrganizers = async (page: number, search?: string, limit: number = 10) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search ? { search } : {}),
      });

      const response = await api.get(`/users/organizers?${params}`);

      const organizersData = response.data?.data?.data || response.data?.data || [];
      const paginationData = response.data?.data?.pagination || response.data?.pagination;

      const data = organizersData.map((org: any) => {
        const firstName = org.details?.first_name || '';
        const lastName = org.details?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          value: org.id.toString(),
          label: fullName || org.phone_number || `User ${org.id}`,
          description: org.phone_number, // Show phone as description
          data: org,
        };
      });

      const pagination = {
        current_page: paginationData?.current_page || page,
        per_page: paginationData?.per_page || limit,
        total: paginationData?.total || 0,
        total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
        has_next_page: paginationData?.has_next_page || false,
        has_prev_page: paginationData?.has_prev_page || page > 1,
        next_page: paginationData?.next_page || null,
        prev_page: paginationData?.prev_page || null,
      };

      return { data, pagination };
    } catch (error: any) {
      console.error("Error fetching organizers:", error);
      throw error;
    }
  };

  const fetchGrounds = async (page: number, search?: string, limit: number = 10) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search ? { search } : {}),
      });

      const response = await api.get(`/grounds/name?${params}`);

      const groundsData = response.data?.data?.data || response.data?.data || [];
      const paginationData = response.data?.data?.pagination || response.data?.pagination;

      const data = groundsData.map((ground: any) => ({
        value: ground.id.toString(),
        label: ground.name,
        description: `${ground.city || ''}, ${ground.country || ''} • ${ground.pitch_type || ''}`.trim(),
        data: ground,
      }));

      const pagination = {
        current_page: paginationData?.current_page || page,
        per_page: paginationData?.per_page || limit,
        total: paginationData?.total || 0,
        total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
        has_next_page: paginationData?.has_next_page || false,
        has_prev_page: paginationData?.has_prev_page || page > 1,
        next_page: paginationData?.next_page || null,
        prev_page: paginationData?.prev_page || null,
      };

      return { data, pagination };
    } catch (error: any) {
      console.error("Error fetching grounds:", error);
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center mb-6">
        <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
        <h2 className="text-xl font-bold text-primary-950 flex items-center">
          Create Tournament
        </h2>
      </div>

      <div className="mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Header showing create mode */}
        <div className="border-b border-gray-100 pb-4 mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Tournament Setup
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormInput
                label="Tournament Name"
                value={formData.name}
                onChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
                placeholder="Enter tournament name"
                error={errors.name}
                required
              />
            </div>

            <div className="md:col-span-2">
              <FormInput
                label="Description"
                value={formData.description}
                onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                placeholder="Enter tournament description"
              />
            </div>

            <ModalSelect
              label="Select Organizer"
              placeholder="Choose an organizer"
              value={formData.organizer_id}
              onChange={(option) => {
                setFormData((prev) => ({ ...prev, organizer_id: option.value as any }));
              }}
              fetchOptions={fetchOrganizers}
              required
              searchPlaceholder="Search by name or phone..."
              error={errors.organizer_id}
              renderOption={(option) => (
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500 capitalize flex items-center mt-0.5">
                    {option.description}
                  </div>
                </div>
              )}
            />

            <CustomSelect
              label="Tournament Category"
              options={tournamentCategories}
              value={formData.tournament_category}
              onChange={(value) => setFormData((prev) => ({ ...prev, tournament_category: value as any }))}
              placeholder="Select category"
              searchable
            />

            <CustomSelect
              label="Tournament Status"
              options={tournamentStatuses}
              value={formData.status}
              onChange={(value) => setFormData((prev) => ({ ...prev, status: value as any }))}
              placeholder="Select status"
              searchable
            />

            {/* Ground Selector */}
            <ModalSelect
              label="Ground Name"
              placeholder="Select a ground"
              value={formData.ground_id}
              onChange={(option) => {
                const selectedGround = option.data;
                setFormData(prev => ({
                  ...prev,
                  ground_id: option.value,
                  location: selectedGround?.city || '',
                  pitch_type: selectedGround?.pitch_type || ''
                }));
              }}
              fetchOptions={fetchGrounds}
              required
              searchPlaceholder="Search grounds..."
              error={errors.ground_id}
              renderOption={(option) => (
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500 flex items-center mt-0.5">
                    {option.description}
                  </div>
                </div>
              )}
            />

            <div className="relative">
              <FormInput
                label="Location"
                value={formData.location || ''}
                onChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
                placeholder="Enter tournament location"
                error={errors.location}
                required
              />
              <FiMapPin className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>
          </div>

          {/* Date Information */}
          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FiCalendar className="w-5 h-5 mr-2 text-primary-600" />
              Tournament Dates
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <FormInput
                  label="Start Date"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(value) => setFormData((prev) => ({ ...prev, start_date: value }))}
                  error={errors.start_date}
                  required
                  min={new Date().toISOString().slice(0, 16)}
                />
                <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>

              <div className="relative">
                <FormInput
                  label="End Date"
                  type="datetime-local"
                  value={formData.end_date || ''}
                  onChange={(value) => setFormData((prev) => ({ ...prev, end_date: value }))}
                  required
                  error={errors.end_date}
                  min={formData.start_date || new Date().toISOString().slice(0, 16)}
                />
                <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>

              <div className="relative">
                <FormInput
                  label="Registration Deadline"
                  type="datetime-local"
                  value={formData.registration_deadline || ''}
                  onChange={(value) => setFormData((prev) => ({ ...prev, registration_deadline: value }))}
                  error={errors.registration_deadline}
                  max={formData.start_date || undefined}
                  min={new Date().toISOString().slice(0, 16)}
                />
                <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Tournament Configuration */}
          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Tournament Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <FormInput
                label="Maximum Teams"
                type="number"
                value={formData.max_teams || ''}
                onChange={(value) => setFormData((prev) => ({ ...prev, max_teams: value }))}
                placeholder="Enter max teams (optional)"
                error={errors.max_teams}
                min={2}
                max={128}
              /> */}

              <CustomSelect
                label="Match Type"
                options={matchTypes}
                value={formData.match_type}
                onChange={(value) => setFormData((prev) => ({ ...prev, match_type: value as any }))}
                placeholder="Select match type"
                required
                error={errors.match_type}
                searchable
              />

              <CustomSelect
                label="Ball Type"
                options={ballTypes}
                value={formData.ball_type}
                onChange={(value) => setFormData((prev) => ({ ...prev, ball_type: value as any }))}
                placeholder="Select ball type"
                searchable
                required
                error={errors.ball_type}
              />

              <CustomSelect
                label="Pitch Type"
                options={pitchTypes}
                value={formData.pitch_type}
                onChange={(value) => setFormData((prev) => ({ ...prev, pitch_type: value as any }))}
                placeholder="Select pitch type"
                searchable
                required
                error={errors.pitch_type}
              />
            </div>
          </div>

          {/* Financial Information */}
          {/* <div className="border-t border-gray-100 pt-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              Financial Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <FormInput
                  label="Entry Fee (₹)"
                  type="number"
                  value={formData.entry_fee || ''}
                  onChange={(value) => setFormData((prev) => ({ ...prev, entry_fee: value }))}
                  placeholder="Enter entry fee (optional)"
                  error={errors.entry_fee}
                  min={0}
                />
              </div>

              <div className="relative">
                <FormInput
                  label="Prize Pool (₹)"
                  type="number"
                  value={formData.prize_pool || ''}
                  onChange={(value) => setFormData((prev) => ({ ...prev, prize_pool: value }))}
                  placeholder="Enter prize pool (optional)"
                  error={errors.prize_pool}
                  min={0}
                />
              </div>
            </div>
          </div> */}

          {/* Media and Additional Information */}
          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Media & Additional Details</h4>

            {/* Banner and Logo Upload */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
              {/* Banner Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tournament Banner
                  <span className="text-xs text-gray-500 ml-1">(Recommended: 16:9 ratio)</span>
                </label>
                <div className="space-y-3">
                  {bannerPreview ? (
                    <div className="relative group">
                      <img
                        src={bannerPreview}
                        alt="Banner preview"
                        className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={removeBanner}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                      <div className="text-center">
                        <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                        <span className="text-sm text-gray-500">Preview banner</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerFileChange}
                      className="hidden"
                      id="banner-upload"
                    />
                    <label
                      htmlFor="banner-upload"
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-sm"
                    >
                      <FiUpload className="w-4 h-4" />
                      <span>Choose Banner</span>
                    </label>
                    {bannerUploading && (
                      <span className="text-sm text-blue-600 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Uploading...
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Maximum file size: 2MB. Supported formats: JPG, PNG, WEBP</p>
                </div>
              </div>

              {/* Logo Upload - Square Shape */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tournament Logo
                  <span className="text-xs text-gray-500 ml-1">(Square recommended)</span>
                </label>
                <div className="space-y-3">
                  {logoPreview ? (
                    <div className="relative group">
                      <div className="w-32 h-32">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-full h-full object-cover rounded-lg border border-gray-200 shadow-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute top-2 left-24 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                      <div className="text-center">
                        <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                        <span className="text-xs text-gray-500">Preview logo</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-sm"
                    >
                      <FiUpload className="w-4 h-4" />
                      <span>Choose Logo</span>
                    </label>
                    {logoUploading && (
                      <span className="text-sm text-blue-600 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Uploading...
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 text-left">Maximum file size: 2MB. Supported formats: JPG, PNG, WEBP</p>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <FormInput
                label="Rules & Regulations"
                value={formData.rules || ''}
                onChange={(value) => setFormData((prev) => ({ ...prev, rules: value }))}
                placeholder="Enter tournament rules and regulations (optional)"
                error={errors.rules}
              />
            </div>
          </div>

          {/* Tournament Settings */}
          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Tournament Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <FormCheckbox
                  label="Last Batter Rule"
                  checked={formData.last_batter_rule}
                  onChange={(checked) => setFormData((prev) => ({ ...prev, last_batter_rule: checked }))}
                  description="Enable special last batter rule for this tournament"
                />
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <FormCheckbox
                  label="Allow More Teams"
                  checked={formData.more_teams}
                  onChange={(checked) => setFormData((prev) => ({ ...prev, more_teams: checked }))}
                  description="Allow registration beyond the maximum teams limit"
                />
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <FormCheckbox
                  label="Match Officials Required"
                  checked={formData.match_officials}
                  onChange={(checked) => setFormData((prev) => ({ ...prev, match_officials: checked }))}
                  description="Require professional match officials for games"
                />
              </div>
            </div>
          </div>

          {apiError && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-start space-x-2">
              <div className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0">⚠️</div>
              <div>{apiError}</div>
            </div>
          )}

          <div className="flex justify-end pt-6 border-t border-gray-100 space-x-4">
            <button
              type="button"
              onClick={() => navigate("/tournaments")}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || bannerUploading || logoUploading}
              className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium min-w-[160px]"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </span>
              ) : bannerUploading || logoUploading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </span>
              ) : (
                "Create Tournament"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}